import { TinyColor } from '@ctrl/tinycolor'
import { AbsoluteFill } from 'remotion'
import ZealousSvg from '../assets/ZealousSvg'
import { FallbackImg } from '../components/FallbackImg'
import { BarsVisualization } from '../components/waveform/BarsVisualization'
import { LayoutInfo, LayoutProps } from '../types'
import { defaultClipScene } from '../types/defaultClipScene'
import { cn } from '../utils/cn'
import { useOrientation } from '../utils/useOrientation'
import { sceneAudioSrc } from '../utils/sceneAudioSrc'

function ScriptSmall({
  clipScene,
  children,
  orientation,
  speaker,
  style,
}: LayoutProps) {
  const { p, s, l, lp, sp } = useOrientation(orientation)

  return (
    <AbsoluteFill className="justify-between p-[50px]">
      <div className="flex items-center justify-between">
        <div
          className={cn(
            'flex shrink-0 items-center rounded-full border-2',
            s && 'space-x-[16px] p-[16px]',
            lp && 'space-x-[20px] p-[20px]',
          )}
          style={{
            borderColor: new TinyColor(style.border.color)
              .setAlpha(0.5)
              .toRgbString(),
          }}
        >
          <FallbackImg
            src={speaker?.avatarUrl}
            className={cn(
              'aspect-square rounded-full object-cover',
              s && 'h-[62px]',
              lp && 'h-[72px]',
            )}
          />

          <p
            style={{ ...style.speakerUsername }}
            className={cn(
              'line-clamp-1 leading-[1.26]',
              s && 'text-[32px]',
              lp && 'text-[34px]',
            )}
          >
            {speaker?.username}
          </p>
        </div>

        {orientation === 'landscape' && (
          <div className="">
            <BarsVisualization
              color={style.waveform.color}
              gapSize={11}
              height={160}
              lineThickness={12}
              mediaStart={clipScene.mediaStart}
              mediaUrl={sceneAudioSrc(clipScene)}
              waveformUrl={clipScene.waveformUrl}
              roundness={6}
              width={1200}
            />
          </div>
        )}

        {clipScene.showZealousLogo && (
          <ZealousSvg
            color={style.logo.color}
            className="h-[49px] shrink-0 opacity-50"
          />
        )}
      </div>

      <div className={cn(lp && 'px-[50px]')}>{children}</div>

      {orientation !== 'landscape' && (
        <div className={cn(p && 'px-[50px]')}>
          <BarsVisualization
            color={style.waveform.color}
            gapSize={8}
            height={220}
            lineThickness={11}
            roundness={5}
            mediaStart={clipScene.mediaStart}
            mediaUrl={sceneAudioSrc(clipScene)}
            waveformUrl={clipScene.waveformUrl}
          />
        </div>
      )}

      <div
        style={{
          borderColor: new TinyColor(style.border.color)
            .setAlpha(0.5)
            .toRgbString(),
        }}
        className={cn(
          'flex items-center rounded-[50px] border-2 p-[30px]',
          l && 'space-x-[40px]',
          sp && 'space-x-[30px]',
        )}
      >
        <FallbackImg
          src={clipScene.imageUrl}
          className={cn(
            'aspect-square rounded-[30px] object-cover',
            s && 'h-[135px]',
            lp && 'h-[150px]',
          )}
        />

        <div className="leading-[1.62] font-medium">
          <p
            style={{ ...style.title }}
            className="line-clamp-1 text-left text-[34px]"
          >
            {clipScene.title}
          </p>

          <p
            style={{ ...style.subtitle }}
            className="line-clamp-1 text-left text-[32px]"
          >
            {clipScene.subtitle}
          </p>
        </div>
      </div>
    </AbsoluteFill>
  )
}

export const scriptSmallLayoutInfo: LayoutInfo = {
  name: 'scriptSmall',
  Component: ScriptSmall,
  defaultScene: {
    ...defaultClipScene,
    layout: 'scriptSmall',
  },
  defaultStyle: {
    background: { color: 'primary' },
    border: { color: 'secondary' },
    logo: { color: 'secondary' },
    waveform: { color: 'accent' },
    subtitle: {
      color: 'secondary',
      fontFamily: 'primary',
      opacity: 0.5,
    },
    title: {
      color: 'secondary',
      fontFamily: 'primary',
    },
    speakerUsername: { color: 'secondary', fontFamily: 'primary' },
    transcription: {
      highlightColor: 'accent',
      color: 'secondary',
      scrollType: 'line-by-line',
      fontFamily: 'primary',
      highlightType: 'last-word',
      fontSize: 51,
      fontWeight: 500,
      linesPerPage: 3,
      lineHeight: 1.7,
      textAlign: 'left',
      unPlayedColor: 'secondary',
      unPlayedOpacity: 0.5,
    },
  },
  transcriptions: {
    square: {},
    landscape: {
      fontSize: 62,
      lineHeight: 1.9,
      linesPerPage: 4,
    },
    portrait: {
      fontSize: 68,
      lineHeight: 2,
      linesPerPage: 7,
    },
  },
}
